import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * AI transcript v2 API client (api-ai-transcript-v2-service). Thirteenth
 * of the v2 fleet. Wraps the endpoints the AI-transcript modal drives.
 *
 * Browser-mode (nutritionist / physiotherapist path): the CM joins the
 * LiveKit room in-browser via WebRTC; the patient is dialled via LK-SIP
 * once the browser confirms its mic is live. The modal handles the
 * WebRTC connection using livekit-client; this service is still HTTP-only.
 *
 *   S0 list       -> list(patientId)
 *   S1 initiate   -> initiate(patientId, {patientCountryCode, patientPhoneNo})
 *                    → {transcriptId, callSid, livekitUrl, livekitRoom,
 *                       livekitToken, tokenExpiresAt}. Care-manager phone
 *                      is looked up on the Go side from the authenticated
 *                      employee's profile — never sent from here.
 *   S1 join       -> browser connects to livekitUrl using livekitToken,
 *                    publishes its local mic.
 *   S1 dial       -> dialPatient(patientId, transcriptId) — fired once
 *                    the browser is in the room with a live mic. Server
 *                    places the patient SIP INVITE.
 *   Poll          -> get(patientId, transcriptId). Response includes:
 *                    - callState: "" | "waiting_cm" | "ringing_patient"
 *                                 | "connected" | "dropped"  (drives S1/S2/S3)
 *                    - version, turns[], captured[]  (drives review flow)
 *   Consent gate  -> consent(patientId, transcriptId, {captured, expectedVersion})
 *   Card action   -> decideCaptured(patientId, transcriptId, capturedId,
 *                                   {newState, payload?, expectedVersion})
 *   Save section  -> saveSection(patientId, transcriptId, section,
 *                                {skipOnFailure, expectedVersion})
 *
 * All mutating endpoints echo the fresh Transcript document + a `case`
 * label; 409 on version mismatch (caller refetches). appendTurn is not
 * exposed here — turns are appended server-side by the Python agent.
 */
class AiTranscriptService {
  constructor() {
    this.apiService = new APIService();
  }

  /**
   * S0 list. Optional `appointmentId` scopes the response to a single
   * appointment on the patient — the modal opens AFTER an appointment
   * is picked, so the past-transcripts rail should only show calls
   * placed against THAT appointment. Falsy (0 / null / undefined)
   * omits the filter and returns every transcript for the patient
   * (legacy behavior for any consumer without appointment context).
   */
  list(patientId, appointmentId, onResponse, onError) {
    let url = APIConfig.aiTranscriptV2.list.format(patientId);
    const aptId = Number(appointmentId);
    if (Number.isFinite(aptId) && aptId > 0) {
      url += `?appointmentId=${aptId}`;
    }
    return this.apiService.get(url, onResponse, onError);
  }

  get(patientId, transcriptId, onResponse, onError) {
    const url = APIConfig.aiTranscriptV2.get.format(patientId, transcriptId);
    return this.apiService.get(url, onResponse, onError);
  }

  initiate(patientId, body, onResponse, onError) {
    const url = APIConfig.aiTranscriptV2.initiate.format(patientId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Browser-mode: place the patient SIP INVITE. Called by the modal
   * once the browser has joined the LK room and its mic is publishing.
   * Idempotent server-side; a second call returns 202 without re-dialing.
   * Empty body — the server has everything it needs on the Transcript doc.
   */
  dialPatient(patientId, transcriptId, onResponse, onError) {
    const url = APIConfig.aiTranscriptV2.dialPatient.format(patientId, transcriptId);
    return this.apiService.post(url, {}, onResponse, onError);
  }

  /**
   * Fetch a short-lived (~15 min) signed GET URL for the audio recording
   * of a past call. `recordingId` is the numeric id stamped on
   * transcript.callRecording.recordingId after LK egress completes and
   * document-service's register-existing round-trip succeeds.
   *
   * Reuses the same endpoint appointment details plays sparktg
   * recordings through (myPlan.navigo.getCallRecordingSignedUrl), so
   * one signed-URL pipeline serves both vendors. Response shape:
   *   { url, method: "GET", contentType, expiresAt, expiresInSeconds }
   *
   * Fetch on click, not per row — the URL expires and prefetching a
   * table's worth hands out URLs that are dead before they're used.
   */
  getRecordingDownloadUrl(recordingId, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getCallRecordingSignedUrl.format(recordingId);
    return this.apiService.get(url, onResponse, onError);
  }

  consent(patientId, transcriptId, body, onResponse, onError) {
    const url = APIConfig.aiTranscriptV2.consent.format(patientId, transcriptId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  proposeCaptured(patientId, transcriptId, body, onResponse, onError) {
    const url = APIConfig.aiTranscriptV2.proposeCaptured.format(patientId, transcriptId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  decideCaptured(patientId, transcriptId, capturedId, body, onResponse, onError) {
    const url = APIConfig.aiTranscriptV2.decideCaptured.format(
      patientId, transcriptId, capturedId,
    );
    return this.apiService.post(url, body, onResponse, onError);
  }

  end(patientId, transcriptId, body, onResponse, onError) {
    const url = APIConfig.aiTranscriptV2.end.format(patientId, transcriptId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  saveSection(patientId, transcriptId, section, body, onResponse, onError) {
    const url = APIConfig.aiTranscriptV2.saveSection.format(
      patientId, transcriptId, section,
    );
    return this.apiService.post(url, body, onResponse, onError);
  }
}

export default AiTranscriptService;
